/**
 * NumberUtils - Comprehensive number and currency formatting utilities
 * 
 * Features:
 * - Percentage formatting with proper decimals
 * - Currency formatting for Bulgarian Lev (BGN) and Euro (EUR)
 * - Odds formatting for betting coefficients
 * - Number validation and parsing
 * - Mathematical utilities for calculations
 * - Number comparison and sorting utilities
 */

/**
 * Currency codes
 */
export const CURRENCY_CODES = {
  BGN: 'BGN', // Bulgarian Lev
  EUR: 'EUR', // Euro
  USD: 'USD'  // US Dollar (for future use)
};

/**
 * Currency symbols
 */
export const CURRENCY_SYMBOLS = {
  BGN: 'лв',
  EUR: '€',
  USD: '$'
};

/**
 * Currency names in different languages
 */
const CURRENCY_NAMES = {
  bg: {
    BGN: 'лева',
    EUR: 'евро',
    USD: 'долара'
  },
  en: {
    BGN: 'lev',
    EUR: 'euros',
    USD: 'dollars'
  }
};

/**
 * Locale-specific number formatting
 */
const LOCALE_FORMATS = {
  bg: {
    decimalSeparator: ',',
    thousandsSeparator: ' ',
    currencyPosition: 'after', // "100 лв" format
    percentagePosition: 'after' // "5,5%" format
  },
  en: {
    decimalSeparator: '.',
    thousandsSeparator: ',',
    currencyPosition: 'before', // "$100" format
    percentagePosition: 'after' // "5.5%" format
  }
};

/**
 * Default formatting options
 */
const DEFAULT_OPTIONS = {
  decimals: 2,
  minDecimals: 0,
  maxDecimals: 2,
  showTrailingZeros: false,
  useGrouping: true
};

/**
 * NumberUtils class with comprehensive number utilities
 */
class NumberUtils {
  /**
   * Parse a string or number to a number
   * @param {string|number} value - Value to parse
   * @param {number} defaultValue - Default value if parsing fails
   * @returns {number} Parsed number or defaultValue
   */
  static parseNumber(value, defaultValue = 0) {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }
    
    if (typeof value === 'number') {
      return isNaN(value) ? defaultValue : value;
    }
    
    if (typeof value === 'string') {
      // Remove spaces and replace comma with dot for parsing
      const cleaned = value.trim().replace(/\s/g, '').replace(',', '.');
      const parsed = parseFloat(cleaned);
      
      if (isNaN(parsed)) {
        return defaultValue;
      }
      
      return parsed;
    }
    
    return defaultValue;
  }

  /**
   * Validate if a value is a valid number
   * @param {any} value - Value to validate
   * @returns {boolean} True if valid number
   */
  static isValidNumber(value) {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    
    if (typeof value === 'number') {
      return !isNaN(value) && isFinite(value);
    }
    
    if (typeof value === 'string') {
      const cleaned = value.trim().replace(/\s/g, '').replace(',', '.');
      const parsed = parseFloat(cleaned);
      return !isNaN(parsed) && isFinite(parsed);
    }
    
    return false;
  }

  /**
   * Format a number with specified options
   * @param {number|string} value - Number to format
   * @param {Object} options - Formatting options
   * @param {number} options.decimals - Number of decimal places
   * @param {number} options.minDecimals - Minimum decimal places
   * @param {number} options.maxDecimals - Maximum decimal places
   * @param {boolean} options.showTrailingZeros - Show trailing zeros
   * @param {boolean} options.useGrouping - Use thousand separators
   * @param {string} options.language - Language code ('bg' or 'en')
   * @returns {string} Formatted number string
   */
  static formatNumber(value, options = {}) {
    const num = this.parseNumber(value);
    if (num === 0 && !this.isValidNumber(value)) {
      return '0';
    }
    
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const locale = LOCALE_FORMATS[opts.language || 'bg'];
    
    // Determine decimal places
    let decimals = opts.decimals;
    if (opts.minDecimals !== undefined || opts.maxDecimals !== undefined) {
      // Count actual decimal places needed
      const numStr = num.toString();
      const decimalPart = numStr.split('.')[1] || '';
      const actualDecimals = decimalPart.length;
      
      if (opts.minDecimals !== undefined) {
        decimals = Math.max(decimals, opts.minDecimals, actualDecimals);
      }
      if (opts.maxDecimals !== undefined) {
        decimals = Math.min(decimals, opts.maxDecimals, actualDecimals);
      }
    }
    
    // Format number
    const fixed = num.toFixed(decimals);
    let formatted = fixed;
    
    // Split integer and decimal parts
    const parts = formatted.split('.');
    let integerPart = parts[0];
    let decimalPart = parts[1] || '';
    
    // Apply thousand separators
    if (opts.useGrouping) {
      const negative = integerPart.startsWith('-');
      const absInteger = negative ? integerPart.slice(1) : integerPart;
      const grouped = absInteger.replace(/\B(?=(\d{3})+(?!\d))/g, locale.thousandsSeparator);
      integerPart = negative ? '-' + grouped : grouped;
    }
    
    // Combine parts
    if (decimalPart) {
      // Remove trailing zeros if not needed
      if (!opts.showTrailingZeros) {
        decimalPart = decimalPart.replace(/0+$/, '');
      }
      formatted = decimalPart ? `${integerPart}${locale.decimalSeparator}${decimalPart}` : integerPart;
    } else {
      formatted = integerPart;
    }
    
    return formatted;
  }

  /**
   * Format percentage
   * @param {number|string} value - Percentage value (e.g., 5.5 for 5.5%)
   * @param {Object} options - Formatting options
   * @param {number} options.decimals - Decimal places (default: 2)
   * @param {boolean} options.showSymbol - Show % symbol (default: true)
   * @param {string} options.language - Language code ('bg' or 'en')
   * @returns {string} Formatted percentage string
   */
  static formatPercentage(value, options = {}) {
    const opts = {
      decimals: 2,
      showSymbol: true,
      language: 'bg',
      ...options
    };
    
    const num = this.parseNumber(value);
    const formatted = this.formatNumber(num, {
      decimals: opts.decimals,
      showTrailingZeros: false,
      language: opts.language
    });
    
    if (opts.showSymbol) {
      return `${formatted}%`;
    }
    
    return formatted;
  }

  /**
   * Format currency
   * @param {number|string} value - Amount to format
   * @param {string} currency - Currency code (BGN, EUR, USD)
   * @param {Object} options - Formatting options
   * @param {number} options.decimals - Decimal places (default: 2)
   * @param {boolean} options.showSymbol - Show currency symbol (default: true)
   * @param {boolean} options.showCode - Show currency code (default: false)
   * @param {string} options.language - Language code ('bg' or 'en')
   * @returns {string} Formatted currency string
   */
  static formatCurrency(value, currency = CURRENCY_CODES.BGN, options = {}) {
    const opts = {
      decimals: 2,
      showSymbol: true,
      showCode: false,
      language: 'bg',
      ...options
    };
    
    const num = this.parseNumber(value);
    const formatted = this.formatNumber(num, {
      decimals: opts.decimals,
      showTrailingZeros: false,
      useGrouping: true,
      language: opts.language
    });
    
    const locale = LOCALE_FORMATS[opts.language];
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    const code = currency;
    
    let result = formatted;
    
    if (opts.showCode) {
      // Show currency code
      if (locale.currencyPosition === 'after') {
        result = `${formatted} ${code}`;
      } else {
        result = `${code} ${formatted}`;
      }
    } else if (opts.showSymbol) {
      // Show currency symbol
      if (locale.currencyPosition === 'after') {
        result = `${formatted} ${symbol}`;
      } else {
        result = `${symbol}${formatted}`;
      }
    }
    
    return result;
  }

  /**
   * Format Bulgarian Lev (BGN)
   * @param {number|string} value - Amount to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted BGN string (e.g., "100,50 лв")
   */
  static formatBGN(value, options = {}) {
    return this.formatCurrency(value, CURRENCY_CODES.BGN, options);
  }

  /**
   * Format Euro (EUR)
   * @param {number|string} value - Amount to format
   * @param {Object} options - Formatting options
   * @returns {string} Formatted EUR string (e.g., "100,50 €")
   */
  static formatEUR(value, options = {}) {
    return this.formatCurrency(value, CURRENCY_CODES.EUR, options);
  }

  /**
   * Format betting odds (coefficient)
   * @param {number|string} value - Odds value to format
   * @param {Object} options - Formatting options
   * @param {number} options.decimals - Decimal places (default: 2)
   * @param {boolean} options.showSymbol - Show "x" or ":" prefix (default: false)
   * @param {string} options.format - Format type: 'decimal' (default), 'fractional', 'american'
   * @returns {string} Formatted odds string
   */
  static formatOdds(value, options = {}) {
    const opts = {
      decimals: 2,
      showSymbol: false,
      format: 'decimal',
      ...options
    };
    
    const num = this.parseNumber(value);
    
    if (opts.format === 'fractional') {
      // Convert decimal to fractional (e.g., 2.5 -> 3/2)
      return this.decimalToFractional(num, opts.decimals);
    } else if (opts.format === 'american') {
      // Convert decimal to American (e.g., 2.5 -> +150)
      return this.decimalToAmerican(num);
    } else {
      // Decimal format (default)
      const formatted = this.formatNumber(num, {
        decimals: opts.decimals,
        showTrailingZeros: false,
        useGrouping: false
      });
      
      if (opts.showSymbol) {
        return `${formatted}x`;
      }
      
      return formatted;
    }
  }

  /**
   * Convert decimal odds to fractional format
   * @param {number} decimal - Decimal odds
   * @param {number} precision - Precision for conversion
   * @returns {string} Fractional odds (e.g., "3/2")
   */
  static decimalToFractional(decimal, precision = 2) {
    const num = this.parseNumber(decimal);
    
    if (num <= 1) {
      return '1/1';
    }
    
    // Convert to fractional (decimal - 1)
    const fractional = num - 1;
    
    // Find common fraction
    const tolerance = Math.pow(10, -precision);
    let denominator = 1;
    let numerator = Math.round(fractional * denominator);
    
    while (Math.abs(fractional - numerator / denominator) > tolerance && denominator < 1000) {
      denominator++;
      numerator = Math.round(fractional * denominator);
    }
    
    // Simplify fraction
    const gcd = this.gcd(numerator, denominator);
    const simplifiedNumerator = numerator / gcd;
    const simplifiedDenominator = denominator / gcd;
    
    return `${simplifiedNumerator}/${simplifiedDenominator}`;
  }

  /**
   * Convert decimal odds to American format
   * @param {number} decimal - Decimal odds
   * @returns {string} American odds (e.g., "+150" or "-200")
   */
  static decimalToAmerican(decimal) {
    const num = this.parseNumber(decimal);
    
    if (num < 2) {
      // Favorite (negative)
      const american = Math.round(-100 / (num - 1));
      return american.toString();
    } else {
      // Underdog (positive)
      const american = Math.round((num - 1) * 100);
      return `+${american}`;
    }
  }

  /**
   * Calculate greatest common divisor
   * @param {number} a - First number
   * @param {number} b - Second number
   * @returns {number} GCD
   */
  static gcd(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    
    while (b !== 0) {
      const temp = b;
      b = a % b;
      a = temp;
    }
    
    return a;
  }

  /**
   * Round a number to specified decimal places
   * @param {number} value - Number to round
   * @param {number} decimals - Decimal places (default: 2)
   * @returns {number} Rounded number
   */
  static round(value, decimals = 2) {
    const num = this.parseNumber(value);
    const factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
  }

  /**
   * Floor a number to specified decimal places
   * @param {number} value - Number to floor
   * @param {number} decimals - Decimal places (default: 2)
   * @returns {number} Floored number
   */
  static floor(value, decimals = 2) {
    const num = this.parseNumber(value);
    const factor = Math.pow(10, decimals);
    return Math.floor(num * factor) / factor;
  }

  /**
   * Ceil a number to specified decimal places
   * @param {number} value - Number to ceil
   * @param {number} decimals - Decimal places (default: 2)
   * @returns {number} Ceiled number
   */
  static ceil(value, decimals = 2) {
    const num = this.parseNumber(value);
    const factor = Math.pow(10, decimals);
    return Math.ceil(num * factor) / factor;
  }

  /**
   * Clamp a number between min and max
   * @param {number} value - Number to clamp
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @returns {number} Clamped number
   */
  static clamp(value, min, max) {
    const num = this.parseNumber(value);
    return Math.max(min, Math.min(max, num));
  }

  /**
   * Check if a number is within a range
   * @param {number} value - Number to check
   * @param {number} min - Minimum value (inclusive)
   * @param {number} max - Maximum value (inclusive)
   * @returns {boolean} True if within range
   */
  static isInRange(value, min, max) {
    const num = this.parseNumber(value);
    return num >= min && num <= max;
  }

  /**
   * Calculate percentage of a value
   * @param {number} value - Total value
   * @param {number} percentage - Percentage (e.g., 5 for 5%)
   * @returns {number} Calculated percentage value
   */
  static calculatePercentage(value, percentage) {
    const num = this.parseNumber(value);
    const percent = this.parseNumber(percentage);
    return (num * percent) / 100;
  }

  /**
   * Calculate what percentage a value is of another value
   * @param {number} value - Value to calculate percentage of
   * @param {number} total - Total value
   * @returns {number} Percentage value
   */
  static getPercentage(value, total) {
    const num = this.parseNumber(value);
    const tot = this.parseNumber(total);
    
    if (tot === 0) return 0;
    
    return (num / tot) * 100;
  }

  /**
   * Calculate sum of an array of numbers
   * @param {Array} values - Array of numbers
   * @returns {number} Sum
   */
  static sum(values) {
    if (!Array.isArray(values)) return 0;
    
    return values.reduce((acc, val) => {
      return acc + this.parseNumber(val);
    }, 0);
  }

  /**
   * Calculate average of an array of numbers
   * @param {Array} values - Array of numbers
   * @returns {number} Average
   */
  static average(values) {
    if (!Array.isArray(values) || values.length === 0) return 0;
    
    const sum = this.sum(values);
    return sum / values.length;
  }

  /**
   * Find minimum value in an array
   * @param {Array} values - Array of numbers
   * @returns {number} Minimum value
   */
  static min(values) {
    if (!Array.isArray(values) || values.length === 0) return 0;
    
    return Math.min(...values.map(val => this.parseNumber(val)));
  }

  /**
   * Find maximum value in an array
   * @param {Array} values - Array of numbers
   * @returns {number} Maximum value
   */
  static max(values) {
    if (!Array.isArray(values) || values.length === 0) return 0;
    
    return Math.max(...values.map(val => this.parseNumber(val)));
  }

  /**
   * Compare two numbers
   * @param {number} a - First number
   * @param {number} b - Second number
   * @param {number} tolerance - Tolerance for comparison (default: 0.0001)
   * @returns {number} -1 if a < b, 0 if equal, 1 if a > b
   */
  static compare(a, b, tolerance = 0.0001) {
    const numA = this.parseNumber(a);
    const numB = this.parseNumber(b);
    
    const diff = numA - numB;
    
    if (Math.abs(diff) < tolerance) {
      return 0;
    }
    
    return diff < 0 ? -1 : 1;
  }

  /**
   * Check if two numbers are approximately equal
   * @param {number} a - First number
   * @param {number} b - Second number
   * @param {number} tolerance - Tolerance for comparison (default: 0.0001)
   * @returns {boolean} True if approximately equal
   */
  static isApproximatelyEqual(a, b, tolerance = 0.0001) {
    return this.compare(a, b, tolerance) === 0;
  }

  /**
   * Sort numbers in ascending order
   * @param {Array} values - Array of numbers
   * @returns {Array} Sorted array
   */
  static sortAscending(values) {
    if (!Array.isArray(values)) return [];
    
    return [...values].sort((a, b) => {
      const numA = this.parseNumber(a);
      const numB = this.parseNumber(b);
      return numA - numB;
    });
  }

  /**
   * Sort numbers in descending order
   * @param {Array} values - Array of numbers
   * @returns {Array} Sorted array
   */
  static sortDescending(values) {
    if (!Array.isArray(values)) return [];
    
    return [...values].sort((a, b) => {
      const numA = this.parseNumber(a);
      const numB = this.parseNumber(b);
      return numB - numA;
    });
  }

  /**
   * Format large numbers with abbreviations (K, M, B)
   * @param {number|string} value - Number to format
   * @param {Object} options - Formatting options
   * @param {number} options.decimals - Decimal places (default: 1)
   * @param {string} options.language - Language code ('bg' or 'en')
   * @returns {string} Formatted string (e.g., "1.5K", "2.3M")
   */
  static formatCompact(value, options = {}) {
    const opts = {
      decimals: 1,
      language: 'en',
      ...options
    };
    
    const num = Math.abs(this.parseNumber(value));
    const sign = this.parseNumber(value) < 0 ? '-' : '';
    
    const abbreviations = {
      bg: {
        thousand: 'хил',
        million: 'млн',
        billion: 'млрд'
      },
      en: {
        thousand: 'K',
        million: 'M',
        billion: 'B'
      }
    };
    
    const abbrev = abbreviations[opts.language];
    
    if (num >= 1e9) {
      return `${sign}${this.formatNumber(num / 1e9, { decimals: opts.decimals, language: opts.language })}${abbrev.billion}`;
    } else if (num >= 1e6) {
      return `${sign}${this.formatNumber(num / 1e6, { decimals: opts.decimals, language: opts.language })}${abbrev.million}`;
    } else if (num >= 1e3) {
      return `${sign}${this.formatNumber(num / 1e3, { decimals: opts.decimals, language: opts.language })}${abbrev.thousand}`;
    }
    
    return this.formatNumber(value, { language: opts.language });
  }

  /**
   * Pad a number with leading zeros
   * @param {number|string} value - Number to pad
   * @param {number} length - Target length
   * @returns {string} Padded number string
   */
  static padNumber(value, length) {
    const num = this.parseNumber(value).toString();
    return num.padStart(length, '0');
  }

  /**
   * Calculate percentage change between two values
   * @param {number} oldValue - Old value
   * @param {number} newValue - New value
   * @returns {number} Percentage change
   */
  static percentageChange(oldValue, newValue) {
    const old = this.parseNumber(oldValue);
    const newVal = this.parseNumber(newValue);
    
    if (old === 0) {
      return newVal === 0 ? 0 : 100;
    }
    
    return ((newVal - old) / old) * 100;
  }

  /**
   * Calculate compound interest
   * @param {number} principal - Principal amount
   * @param {number} rate - Interest rate (as percentage, e.g., 5 for 5%)
   * @param {number} periods - Number of periods
   * @returns {number} Final amount
   */
  static compoundInterest(principal, rate, periods) {
    const p = this.parseNumber(principal);
    const r = this.parseNumber(rate) / 100;
    const n = this.parseNumber(periods);
    
    return p * Math.pow(1 + r, n);
  }

  /**
   * Format number as ordinal (1st, 2nd, 3rd, etc.)
   * @param {number|string} value - Number to format
   * @param {string} language - Language code ('bg' or 'en')
   * @returns {string} Ordinal number string
   */
  static formatOrdinal(value, language = 'en') {
    const num = Math.abs(this.parseNumber(value));
    
    if (language === 'bg') {
      // Bulgarian ordinal suffixes
      const lastDigit = num % 10;
      const lastTwoDigits = num % 100;
      
      if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return `${num}-ти`;
      } else if (lastDigit === 1) {
        return `${num}-ви`;
      } else if (lastDigit === 2) {
        return `${num}-ри`;
      } else if (lastDigit >= 3 && lastDigit <= 7) {
        return `${num}-ти`;
      } else {
        return `${num}-ти`;
      }
    } else {
      // English ordinal suffixes
      const lastDigit = num % 10;
      const lastTwoDigits = num % 100;
      
      if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return `${num}th`;
      } else if (lastDigit === 1) {
        return `${num}st`;
      } else if (lastDigit === 2) {
        return `${num}nd`;
      } else if (lastDigit === 3) {
        return `${num}rd`;
      } else {
        return `${num}th`;
      }
    }
  }
}

// Export the NumberUtils class and constants
export default NumberUtils;

